import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, HandHeart, ShieldCheck, Timer } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, SectionLabel } from "@/components/Reveal";

const TECH_IMG =
  "https://images.unsplash.com/photo-1617299516258-eb06985065ff?crop=entropy&cs=srgb&fm=jpg&q=85";

const values = [
  {
    icon: HandHeart,
    title: "Kundenzufriedenheit",
    text: "Hohe Kundenzufriedenheit ist unser oberstes Ziel. Wir geben 90 Tage Geld-zurück-Garantie – weil wir hinter unserer Arbeit stehen.",
    big: true,
  },
  {
    icon: BadgeCheck,
    title: "Qualität",
    text: "Original- oder qualitativ gleichwertige Ersatzteile und sauberes Handwerk bei jeder Reparatur.",
  },
  {
    icon: ShieldCheck,
    title: "Ehrlicher Service",
    text: "Transparenter Kostenvoranschlag vor jeder Reparatur. Keine versteckten Kosten, keine Überraschungen.",
  },
  {
    icon: Timer,
    title: "Geschwindigkeit",
    text: "Reparaturen häufig innerhalb von 1 Stunde – dein Alltag wartet nicht, wir auch nicht.",
  },
];

const steps = [
  {
    n: "01",
    title: "Diagnose & Kostenvoranschlag",
    text: "Wir schauen uns dein Gerät an und erstellen einen ehrlichen Kostenvoranschlag nach bestem Wissen.",
  },
  {
    n: "02",
    title: "Express-Reparatur",
    text: "Nach deiner Zustimmung geht es los – viele Reparaturen sind innerhalb von 1 Stunde erledigt.",
  },
  {
    n: "03",
    title: "Qualitätscheck",
    text: "Vor der Übergabe testen wir dein Gerät gründlich: Display, Touch, Akku, Kameras – alles muss sitzen.",
  },
  {
    n: "04",
    title: "Übergabe & Garantie",
    text: "Du bekommst dein Gerät zurück – mit 90 Tagen Geld-zurück-Garantie auf die durchgeführte Reparatur.",
  },
];

const About = () => {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative bg-blueprint pt-40 pb-24 overflow-hidden">
        <div
          className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,230,0,0.1) 0%, transparent 65%)" }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <SectionLabel>Über HeroPhone</SectionLabel>
            <h1 className="mt-5 font-display text-6xl sm:text-7xl lg:text-8xl font-extrabold uppercase text-white leading-[0.9] max-w-4xl">
              Klartext. Qualität. <span className="text-[#FFE600]">Keine Ausreden.</span>
            </h1>
            <p className="mt-7 text-gray-300 text-base sm:text-lg leading-relaxed max-w-2xl">
              HeroPhone ist ein moderner Handy-Reparatur Dienst, der sich auf die
              Reparatur und den Service rund um Smartphones und Tablets
              spezialisiert hat. Unser Ziel ist es, unseren Kunden schnelle,
              zuverlässige und preiswerte Lösungen für ihre mobilen Geräte zu bieten.
            </p>
            <p className="mt-4 text-gray-400 leading-relaxed max-w-2xl">
              Bei HeroPhone stehen Kundenzufriedenheit, Qualität und ehrlicher
              Service an erster Stelle – persönlich betreut von Inhaber Ricardo
              Galindo Cuenca.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:flex items-center justify-center"
          >
            <div
              className="absolute w-[420px] h-[420px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(255,230,0,0.15) 0%, transparent 65%)" }}
            />
            <img
              src="/assets/logo-full-dark.png"
              alt="HeroPhone Logo"
              data-testid="about-hero-logo"
              className="relative w-full max-w-md"
            />
          </motion.div>
        </div>
      </section>

      {/* VALUES BENTO */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Unsere Werte</SectionLabel>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl font-extrabold uppercase text-white leading-[0.92]">
              Woran wir uns <span className="text-[#FFE600]">messen lassen.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className={v.big ? "lg:col-span-2 lg:row-span-1" : ""}>
                <div
                  data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`glow-card rounded-md p-8 sm:p-10 h-full ${
                    v.big ? "bg-[#FFE600]" : "bg-[#111318]"
                  }`}
                >
                  <v.icon
                    className={`w-8 h-8 ${v.big ? "text-[#090A0C]" : "text-[#FFE600]"}`}
                    strokeWidth={1.75}
                  />
                  <h3
                    className={`mt-6 font-display text-3xl sm:text-4xl font-bold uppercase ${
                      v.big ? "text-[#090A0C]" : "text-white"
                    }`}
                  >
                    {v.title}
                  </h3>
                  <p
                    className={`mt-3 text-sm sm:text-base leading-relaxed ${
                      v.big ? "text-[#090A0C]/80" : "text-gray-400"
                    }`}
                  >
                    {v.text}
                  </p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="relative rounded-md overflow-hidden border border-white/15 h-full min-h-[260px]">
                <img
                  src={TECH_IMG}
                  alt="Smartphone-Technologie im Detail"
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="about-values-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#090A0C]/90 via-[#090A0C]/20 to-transparent" />
                <p className="absolute bottom-6 left-6 right-6 font-display text-2xl font-bold uppercase text-white">
                  Moderne Technik, <span className="text-[#FFE600]">ruhige Hand.</span>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Kundenzufriedenheit",
          "Qualität",
          "Ehrlicher Service",
          "Schnell & zuverlässig",
          "Preiswert",
        ]}
      />

      {/* PROCESS */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>So läuft's ab</SectionLabel>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl font-extrabold uppercase text-white leading-[0.92]">
              Vier Schritte zum <span className="text-[#FFE600]">geretteten Gerät.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div data-testid={`process-step-${s.n}`} className="relative border-t-2 border-[#FFE600]/40 pt-8 h-full">
                  <span className="font-display font-extrabold text-6xl text-outline-yellow">{s.n}</span>
                  <h3 className="mt-4 font-display text-2xl font-bold uppercase text-white">{s.title}</h3>
                  <p className="mt-3 text-gray-400 text-sm leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-20 text-center">
            <Link
              to="/kontakt"
              data-testid="about-cta-button"
              className="inline-flex items-center gap-3 bg-[#FFE600] text-[#090A0C] font-display font-bold uppercase tracking-wide text-lg px-8 py-4 rounded-sm hover:bg-[#FFF033] hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
            >
              Überzeug dich selbst
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
